import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';
import { registerPush } from '../utils/push';
import { firebaseAuth } from '../utils/firebase';
import { signInWithCustomToken } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(() =>
    localStorage.getItem('ws_remember') === 'true'
      ? localStorage.getItem('ws_token')
      : sessionStorage.getItem('ws_token')
  );
  const [loading, setLoading] = useState(true);
  const [firebaseReady, setFirebaseReady] = useState(false);

  // remember: true = localStorage, false = sessionStorage, undefined = clear all (logout)
  const saveSessionToken = (token, remember) => {
    setSessionToken(token);
    if (token) {
      if (remember === true) {
        localStorage.setItem('ws_token', token);
        localStorage.setItem('ws_remember', 'true');
        sessionStorage.removeItem('ws_token');
      } else if (remember === false) {
        sessionStorage.setItem('ws_token', token);
        localStorage.removeItem('ws_token');
        localStorage.removeItem('ws_remember');
      }
      // undefined = internal call (checkAuth re-validation), don't change storage
    } else {
      localStorage.removeItem('ws_token');
      localStorage.removeItem('ws_remember');
      sessionStorage.removeItem('ws_token');
    }
  };

  const signInToFirebase = async () => {
    try {
      const res = await authAPI.getFirebaseToken();
      await signInWithCustomToken(firebaseAuth, res.data.firebase_token);
      setFirebaseReady(true);
    } catch (e) {
      // Firebase Auth may not be enabled in the console yet.
      // The app will fall back to polling for real-time updates.
      console.warn('[Firebase] Custom token sign-in skipped - Firestore listeners inactive:', e?.code || e?.message);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      if (response.data?.account_status === 'active') {
        registerPush();
        signInToFirebase();
      }
    } catch (error) {
      setUser(null);
      saveSessionToken(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      saveSessionToken(null);
      setFirebaseReady(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, sessionToken, setSessionToken: saveSessionToken, loading, logout, checkAuth, firebaseReady }}>
      {children}
    </AuthContext.Provider>
  );
};